<template>
  <v-dialog
    :value="isImprimirSecaoDialogOpen"
    max-width="1000px"
    persistent
  >
    <v-card
      elevation="0"
      class="pa-sm-8 pa-2"
    >
      <btn-close-dialog :handle-click="closeDialog" />

      <div ref="pdfContent">
        <v-card-title class="justify-center text-h5 pt-0">
          <v-row class="align-center">
            <v-col cols="4">
              <v-avatar
                rounded
                size="100"
              >
                <img :src="vikLogoUrl" />
              </v-avatar>
            </v-col>

            <v-col
              cols="4"
              class="d-flex justify-center"
            >
              <span>Carta de Intenção</span>
            </v-col>

            <v-col cols="4"></v-col>
          </v-row>
        </v-card-title>

        <v-card-text class="mt-5">
          <v-form class="multi-col-validation">
            <div class="mt-5">
              <tabela-de-celulas
                :ticket-id="ticketId"
                :formulario-id="formularioId"
                :qtd-colunas="qtdColunas"
                :elementos="componentesExcetoAssinaturas"
                :readonly="true"
                :align-center="true"
                :justify-center="true"
                :componente-text-label-options="componenteTextLabelOptions"
              />
            </div>

            <secao-personalizada-impressao-dados-contato
              :solicitante-email="solicitanteData.email"
              :solicitante-telefone="solicitanteData.celular"
            />

            <v-row class="mt-5">
              <v-col
                cols="12"
                class="d-flex justify-center"
              >
                <span>{{ formattedDate }}</span>
              </v-col>
            </v-row>

            <v-row class="align-end">
              <v-col
                cols="6"
                class="d-flex"
              >
                <div class="d-flex flex-column">
                  <span class="align-self-center">{{ atendenteNome }}</span>

                  <v-divider />

                  <span class="align-self-center">Apontador de imóveis VIK Tech and Services</span>
                </div>
              </v-col>

              <v-col
                cols="6"
                class="d-flex justify-end"
              >
                <div class="d-flex flex-column">
                  <v-avatar
                    v-if="assinaturaMaisRecenteUrl"
                    height="100"
                    width="200"
                  >
                    <img :src="assinaturaMaisRecenteUrl" />
                  </v-avatar>

                  <span
                    v-else
                    class="align-self-center"
                  >{{ solicitanteData.nome }}</span>

                  <v-divider />

                  <span class="align-self-center">Proprietário do imóvel</span>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </div>

      <v-card-actions>
        <v-row class="mt-5">
          <v-col
            cols="12"
            class="d-flex justify-center"
          >
            <v-btn
              class="mr-4"
              outlined
              color="secondary"
              @click="closeDialog"
            >
              Cancelar
            </v-btn>

            <v-btn
              color="primary"
              type="submit"
              @click="exportDialogAsPDF"
            >
              Download
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import TabelaDeCelulas from './TabelaDeCelulas.vue'
import useUserStore from '@/store/user'
import SecaoPersonalizadaImpressaoDadosContato from './SecaoPersonalizadaImpressaoDadosContato.vue'
import useAppConfig from '@/@core/@app-config/useAppConfig'
import useDialog from '@/composables/useDialog'
import TiposDeComponentesEnum from '@/models/entities/formulario/componente/TiposDeComponentesEnum'
import DocumentoService from '@/services/modules/DocumentoService'
import ComponenteTextLabelOptions from '@/models/props/ComponenteTextLabelOptions'

export default {
  components: {
    BtnCloseDialog,
    TabelaDeCelulas,
    SecaoPersonalizadaImpressaoDadosContato,
  },

  props: {
    isImprimirSecaoDialogOpen: {
      type: Boolean,
      required: true,
    },
    ticketId: {
      type: Number,
      required: true,
    },
    formularioId: {
      type: Number,
      required: true,
    },
    qtdColunas: {
      type: Number,
      required: true,
    },
    elementos: {
      type: Array,
      required: true,
    },
    solicitanteData: {
      type: Object,
      required: true,
    },
    componenteTextLabelOptions: {
      type: ComponenteTextLabelOptions,
      default: null,
    },
  },

  setup(props, { emit }) {
    const { closeDialog } = useDialog(emit, 'is-imprimir-secao-dialog-open')
    const { nome: atendenteNome } = storeToRefs(useUserStore())

    const componentesExcetoAssinaturas = computed(() => props.elementos.map(linha => linha.filter(comp => comp.idElementoTipo !== TiposDeComponentesEnum.Assinatura)))
    const assinaturaMaisRecenteUrl = ref('')

    watchEffect(async () => {
      if (!props.isImprimirSecaoDialogOpen) return

      const assinatura = props.elementos.flat().filter(it => it.idElementoTipo === TiposDeComponentesEnum.Assinatura)[0]

      if (!assinatura) return null

      const data = await DocumentoService.getFormularioComponentAnexos(props.ticketId, assinatura.id)

      assinaturaMaisRecenteUrl.value = data.pop()?.url
    })

    const formattedDate = ref('')
    onMounted(() => {
      const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
      const currentDate = new Date()

      const day = currentDate.getDate()
      const month = currentDate.getMonth()
      const year = currentDate.getFullYear()

      formattedDate.value = `São Paulo, ${day} de ${months[month]} de ${year}`
    })

    const { isDark } = useAppConfig()
    const pdfContent = ref(null)

    async function exportDialogAsPDF() {
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF('p', 'mm', 'a4')

      if (isDark.value) pdf.setFillColor(49, 45, 75)
      else pdf.setFillColor(255, 255, 255)

      pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F')

      const canvas = await html2canvas(pdfContent.value, {
        useCORS: true,
        backgroundColor: isDark.value ? 'rgb(49, 45, 75)' : 'rgb(255, 255, 255)',
      })

      const imgData = canvas.toDataURL('image/png')

      const pageWidth = pdf.internal.pageSize.getWidth() // Get the width of the page
      const pageHeight = pdf.internal.pageSize.getHeight() // Get the height of the page

      const maxWidth = pageWidth - 20 // Adjust as needed, e.g., subtracting margin
      const maxHeight = pageHeight - 20 // Adjust as needed, e.g., subtracting margin

      const widthRatio = maxWidth / canvas.width
      const heightRatio = maxHeight / canvas.height

      const scalingFactor = Math.min(widthRatio, heightRatio) // Use the minimum ratio to fit within the page

      const imageWidth = canvas.width * scalingFactor
      const imageHeight = canvas.height * scalingFactor

      pdf.addImage(imgData, 'PNG', 10, 10, imageWidth, imageHeight) // Adjust positioning and dimensions
      pdf.save('carta-de-intencao.pdf')
    }

    const vikLogoUrl = 'https://vikdocumentdb.blob.core.windows.net/utils/logo-vik.png?sv=2021-10-04&st=2023-10-25T16%3A05%3A24Z&se=2033-10-26T16%3A05%3A00Z&sr=b&sp=r&sig=j413IXAsVigVba7c7clEiKwl%2F5vzoGVeoiggsFW9ObM%3D'

    return {
      closeDialog,
      atendenteNome,
      componentesExcetoAssinaturas,
      assinaturaMaisRecenteUrl,
      formattedDate,
      pdfContent,
      exportDialogAsPDF,
      vikLogoUrl,
    }
  },
}
</script>
